import { ComponentRef, EventEmitter, Type, ViewContainerRef } from "@angular/core";
import { untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";

/**
 * A factory class for creating components dynamically
 * @param componentRef - The ViewContainerRef where the component will be created
 */
export class ComponentCreationFactory {
    constructor(protected componentRef: ViewContainerRef) {}

    /**
     * Creates a component and sets its inputs, outputs, and async inputs.
     * @param component - The component type to be created.
     * @param inputs - The inputs to be set on the component instance.
     * @param outputs - The outputs to be set on the component instance.
     * @param asyncInputs - The async inputs (by Observable) to be set on the component instance.
     */
    public createComponent<Component>(
        component: Type<Component>,
        inputs?: Partial<Component>,
        outputs?: {
            [K in keyof Component]?: Component[K] extends EventEmitter<infer T> ? (outputValue: T) => void : never;
        },
        asyncInputs?: {
            [K in keyof Component]?: Observable<Component[K] extends infer V ? V : never>;
        }
    ): ComponentRef<Component> | undefined {
        const componentRef = this.componentRef?.createComponent(component);

        if (!componentRef) {
            return;
        }
        const componentInstance = componentRef.instance;

        if (inputs) {
            Object.entries(inputs).forEach(([key, value]) => componentRef.setInput(key, value));
        }

        if (outputs) {
            Object.entries(outputs).forEach(([key, outputFn]) => {
                const keyOutput = key as keyof Component;
                const emitter = componentInstance[keyOutput];
                if (!(emitter instanceof EventEmitter)) {
                    return;
                }
                emitter.pipe(untilDestroyed(componentRef.instance)).subscribe((value) => {
                    (outputFn as (value: unknown) => void)(value);
                });
            });
        }

        if (asyncInputs) {
            Object.entries(asyncInputs).forEach(([key, value]) => {
                (value as Observable<unknown>)
                    .pipe(untilDestroyed(componentRef.instance))
                    .subscribe((asyncValue) => componentRef.setInput(key, asyncValue));
            });
        }

        return componentRef;
    }

    public destroyAll() {
        this.componentRef.clear();
    }
}
